import { MACKEREL_RANKING_POLICY } from "../../features/fishingIntelligence/seaBassPresentation";
import { isMackerelRegional, hasDistinctLegacyIndex, withMackerelScope } from "../../features/fishingIntelligence/mackerelCapabilities";
import React, { useCallback, useEffect, useId, useMemo, useState } from "react";
import { CalendarDays, CircleGauge, Sparkles } from "lucide-react";
import { buildForecastViews } from "../../features/fishingIntelligence/forecastViews";
import { localChartTime, localDayAxis } from "../../features/fishingIntelligence/hourlyChart";
import FishingIntelligenceExplanations from "./FishingIntelligenceExplanations";
import SpeciesIntelligenceTimeline, {
  formatForecastHour,
} from "./SpeciesIntelligenceTimeline";
import "../../styles/components/SpeciesMultiDayForecast.css";

const safeDateFormatter = (locale, options) => {
  try {
    return new Intl.DateTimeFormat(locale || "en", options);
  } catch (_error) {
    return new Intl.DateTimeFormat("en", options);
  }
};

export const formatForecastDayDate = (day, forecastTimezone, locale) => {
  if (day?.windowStart && day?.windowEnd) {
    const format = safeDateFormatter(locale, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: forecastTimezone });
    return `${format.format(new Date(day.windowStart))} – ${format.format(new Date(day.windowEnd))}`;
  }
  const firstTimestamp = day?.points?.[0]?.timestamp;
  const parsed = Date.parse(firstTimestamp);
  if (Number.isFinite(parsed)) {
    try {
      return safeDateFormatter(locale, {
        weekday: "short",
        day: "numeric",
        month: "short",
        timeZone: forecastTimezone,
      }).format(new Date(parsed));
    } catch (_error) {
      // Fall through to a timezone-neutral rendering of the backend local date.
    }
  }
  const match = String(day?.localDate || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return day?.localDate || "";
  return safeDateFormatter(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))));
};

const dayName = (index, day, forecastTimezone, locale, t) => {
  if (day?.viewKey === "rolling") return t("fishing_today_intelligence_next_24_hours");
  if (index === 0) return t("fishing_today_intelligence_today");
  if (index === 1) return t("fishing_today_intelligence_tomorrow");
  return formatForecastDayDate(day, forecastTimezone, locale);
};

const scoreText = (score, t) =>
  score
    ? `${t("fishing_today_intelligence_score_value", { score: score.score })} · ${t(
        `fishing_today_intelligence_score_${score.label}`
      )}`
    : t("fishing_today_intelligence_unavailable");

const confidenceText = (confidence, t) =>
  confidence
    ? `${t("fishing_today_intelligence_score_value", {
        score: confidence.score,
      })} · ${t(`fishing_today_intelligence_confidence_${confidence.level}`)}`
    : t("fishing_today_intelligence_unavailable");

const periodText = (strongest, forecastTimezone, locale) => {
  if (!strongest) return null;
  const start = formatForecastHour(
    strongest.startTimestamp,
    forecastTimezone,
    locale
  );
  if (strongest.kind === "hour") return start;
  const end = formatForecastHour(strongest.endTimestamp, forecastTimezone, locale);
  return `${start}–${end}`;
};

const ForecastDayCard = ({
  day,
  dayIndex,
  forecastTimezone,
  isSelected,
  locale,
  onSelect,
  panelId,
  regional,
  t,
}) => {
  const name = dayName(dayIndex, day, forecastTimezone, locale, t);
  const date = formatForecastDayDate(day, forecastTimezone, locale);
  const strongest = day.strongest;
  const mackerelRanking = regional && day.rankingPolicy === MACKEREL_RANKING_POLICY;
  const distinctLegacy = regional && (day.rankingBasis === "availability_based_outlook" || day.points.some(hasDistinctLegacyIndex));
  return (
    <button
      aria-controls={panelId}
      aria-label={t("fishing_today_intelligence_select_day", { day: name })}
      aria-pressed={isSelected}
      className={`rp-multiday__card ${
        day.isBestModeledPeriod ? "rp-multiday__card--best" : ""
      }`}
      data-local-date={day.localDate}
      onClick={onSelect}
      type="button"
    >
      <span className="rp-multiday__date">
        <strong>{name}</strong>
        {dayIndex < 2 ? <small>{date}</small> : null}
      </span>
      {isSelected ? (
        <span className="rp-multiday__selected-marker">
          {t("fishing_today_intelligence_selected")}
        </span>
      ) : null}
      {day.isBestModeledPeriod ? (
        <span className="rp-multiday__badge">
          <Sparkles size={13} aria-hidden="true" />
          {t("fishing_today_intelligence_best_modeled_period")}
        </span>
      ) : null}
      {!strongest ? (
        <span className="rp-multiday__limited" role="status">
          {t(mackerelRanking ? "mackerel_insufficient_period_hours" : "fishing_today_intelligence_limited_data")}
        </span>
      ) : (
        <>
          <span className="rp-multiday__metric">
            {day.hasFlatOpportunity && !mackerelRanking ? (
              <>
                <small>{t("fishing_today_intelligence_no_standout_period")}</small>
                <strong>
                  {t(distinctLegacy ? "mackerel_similar_legacy" : day.rankingPolicy ? "sea_bass_similar_periods" : "fishing_today_intelligence_similar_opportunity_period")}
                </strong>
              </>
            ) : (
              <>
                <small>
                  {t(
                    mackerelRanking && distinctLegacy ? "mackerel_selected_outlook_period" : strongest.kind === "period"
                      ? "fishing_today_intelligence_strongest_period"
                      : "fishing_today_intelligence_strongest_hour"
                  )}
                </small>
                <strong>{periodText(strongest, forecastTimezone, locale)}</strong>
              </>
            )}
          </span>
          <span className="rp-multiday__metric">
            <small>
              {t(
                mackerelRanking && strongest.kind === "period"
                  ? distinctLegacy ? "mackerel_period_average_outlook" : "mackerel_period_average_availability"
                  : distinctLegacy ? "mackerel_legacy_index" : day.rankingPolicy && strongest.kind === "period"
                  ? "sea_bass_period_average"
                  : day.hasFlatOpportunity
                  ? "fishing_today_intelligence_opportunity"
                  : "fishing_today_intelligence_peak_opportunity"
              )}
            </small>
            <strong>{scoreText(day.rankingPolicy && strongest.kind === "period" ? strongest.meanOpportunity : strongest.peakOpportunity, t)}</strong>
          </span>
          {mackerelRanking && day.hasFlatOpportunity ? (
            <span className="rp-multiday__limited">{t(distinctLegacy ? "mackerel_similar_outlook_periods" : "mackerel_similar_regional_periods")}</span>
          ) : null}
          <span className="rp-multiday__metric rp-multiday__metric--confidence">
            <small>
              <CircleGauge size={13} aria-hidden="true" />
              {t("fishing_today_intelligence_forecast_confidence")}
            </small>
            <strong>{confidenceText(strongest.confidence, t)}</strong>
          </span>
        </>
      )}
      {day.coverageStatus === "partial" ? (
        <span className="rp-multiday__coverage" role="status">
          {t("fishing_today_intelligence_partial_forecast")}
        </span>
      ) : null}
    </button>
  );
};

export default function SpeciesMultiDayForecast({
  context,
  currentTimestamp,
  forecastDays,
  next24Hours,
  selection,
  onSelectionChange,
  onRefresh,
  forecastTimezone,
  locale,
  t,
}) {
  t = withMackerelScope(t, context);
  const sectionId = useId();
  const panelId = `${sectionId}-timeline`;
  const days = useMemo(
    () => buildForecastViews(forecastDays, currentTimestamp, next24Hours, forecastTimezone),
    [currentTimestamp, forecastDays, next24Hours, forecastTimezone]
  );
  const [selectedLocalDate, setSelectedLocalDate] = useState(
    selection?.viewKey || days[0]?.viewKey || null
  );
  const [selectedPoint, setSelectedPoint] = useState(days[0]?.points?.[0] || null);

  useEffect(() => {
    setSelectedLocalDate((previous) => days.some((day) => day.viewKey === previous) ? previous : days[0]?.viewKey || null);
  }, [forecastDays, days]);

  // One normal refresh at the location's next midnight. The server supplies a
  // new reference and summaries; never relabel yesterday's cached Tomorrow.
  useEffect(() => {
    if (!next24Hours?.referenceTime || !onRefresh) return undefined;
    const reference = Date.parse(next24Hours.referenceTime);
    const date = localChartTime(next24Hours.referenceTime, forecastTimezone)?.date;
    const axis = localDayAxis(date, forecastTimezone);
    if (!axis || !Number.isFinite(reference)) return undefined;
    const timer = setTimeout(onRefresh, Math.max(1000, axis.end - reference + 50));
    return () => clearTimeout(timer);
  }, [next24Hours, forecastTimezone, onRefresh]);

  const selectedDay =
    days.find((day) => day.viewKey === selectedLocalDate) || days[0] || null;
  // The cards keep their existing future-only ranking. The chart may use all
  // actual hours returned for this date, including earlier hours when present.
  const chartDay = (Array.isArray(forecastDays) ? forecastDays : [])
    .find((day) => day?.localDate === selectedDay?.localDate);
  const chartPoints = selectedDay?.viewKey === "rolling" ? selectedDay.points : chartDay?.points || [];
  const selectPoint = useCallback((point) => {
    setSelectedPoint(point);
    onSelectionChange?.({ viewKey: selectedDay?.viewKey, timestamp: point?.timestamp || null });
  }, [onSelectionChange, selectedDay?.viewKey]);
  const explanationPoint =
    chartPoints.find((point) => point.timestamp === selectedPoint?.timestamp) ||
    chartPoints[0] ||
    null;
  const mackerelCalendar = days.filter(day => day.rankingPolicy === MACKEREL_RANKING_POLICY);
  const similarMackerelDays = mackerelCalendar.some(day => ["exact_tie", "similar"].includes(day.dayComparison));
  const accessLimitedDays = mackerelCalendar.some(day => day.rankingBasis === "availability_based_outlook");
  if (!days.length) return null;

  return (
    <section className="rp-multiday" aria-labelledby={`${sectionId}-title`}>
      <div className="rp-multiday__heading">
        <CalendarDays size={20} aria-hidden="true" />
        <div>
          <h4 id={`${sectionId}-title`}>
            {t(next24Hours ? "fishing_today_intelligence_forecast_outlook" : "fishing_today_intelligence_next_3_days")}
          </h4>
          <p>{t(days[0]?.rankingPolicy ? "sea_bass_period_note" : "fishing_today_intelligence_period_choice_note")}</p>
          {similarMackerelDays ? (
            <p>{t(accessLimitedDays ? "mackerel_similar_outlook_periods" : "mackerel_similar_regional_periods")}</p>
          ) : days[0]?.rankingPolicy && ["exact_tie", "similar"].includes(days[0]?.dayComparison) ? (
            <p>{t("sea_bass_similar_periods")}</p>
          ) : null}
        </div>
      </div>
      <div
        className="rp-multiday__cards"
        aria-label={t("fishing_today_intelligence_day_selector")}
        role="group"
      >
        {days.map((day, index) => (
          <ForecastDayCard
            day={day}
            dayIndex={index}
            forecastTimezone={forecastTimezone}
            isSelected={day.viewKey === selectedDay?.viewKey}
            key={day.viewKey}
            locale={locale}
            onSelect={() => setSelectedLocalDate(day.viewKey)}
            panelId={panelId}
            regional={isMackerelRegional(context)}
            t={t}
          />
        ))}
      </div>
      <div className="rp-multiday__timeline" id={panelId}>
        <p className="rp-multiday__selected-day" aria-live="polite">
          {t("fishing_today_intelligence_selected_day", {
            day: dayName(
              days.indexOf(selectedDay),
              selectedDay,
              forecastTimezone,
              locale,
              t
            ),
          })}
        </p>
        <SpeciesIntelligenceTimeline
          context={context}
          currentTimestamp={currentTimestamp}
          forecastTimezone={forecastTimezone}
          localDate={selectedDay?.localDate}
          lightCycle={chartDay?.lightCycle}
          lightCycleDays={forecastDays}
          windowStart={selectedDay?.windowStart}
          windowEnd={selectedDay?.windowEnd}
          preferredTimestamp={selection?.timestamp}
          summaryPoints={selectedDay?.points}
          highestHoursLabelKey="fishing_today_intelligence_strongest_hours"
          locale={locale}
          onSelectedPointChange={selectPoint}
          t={t}
          timeline={{ points: chartPoints, rankingPolicy: selectedDay?.rankingPolicy, modeledPeriod: selectedDay?.modeledPeriod, rankingBasis: selectedDay?.rankingBasis, alternativePeriod: selectedDay?.alternativePeriod }}
        />
        {explanationPoint?.timestamp !== currentTimestamp ? (
          <FishingIntelligenceExplanations
            context={context}
            point={explanationPoint}
            t={t}
            variant="selected"
          />
        ) : null}
      </div>
    </section>
  );
}
